import { Box, Typography } from "@mui/material"
import { FilterAltOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useState } from "react"

import { CUSTOMER_ORDER_DATE_RANGE_TYPE_OPTIONS, CUSTOMER_ORDER_STATUS_OPTIONS } from "../../constants"
import { useCustomerOrderList } from "../../hooks/useCustomerOrderList"
import { useSalesOrderPermissions } from "../../hooks/useSalesOrderPermissions"
import { useStartCustomerOrder } from "../../hooks/useStartCustomerOrder"
import { StatusDateRangeFilterDrawer } from "../filters/StatusDateRangeFilterDrawer"

import { CustomerOrderTable } from "./CustomerOrderTable"
import { CustomerOrderFilterChips } from "./filters/CustomerOrderFilterChips"

import { Button } from "@/components/common/Button"
import SearchInput from "@/components/SearchInput/SearchInput"

export const CustomerOrderListPage: FC = () => {
  const [isFilterOpen, setIsFilterOpen] = useState(false)

  const {
    customerOrders,
    isFetching,
    error,
    sorting,
    expanded,
    searchParams,
    tableContainerRef,
    handleSortingChange,
    handleExpandedChange,
    handleSearchChange,
    handleScroll,
    handleFiltersChange,
    handleFilterRemove,
    handleFilterClear,
  } = useCustomerOrderList()

  const { startOrder } = useStartCustomerOrder()
  const { canView } = useSalesOrderPermissions()

  return (
    <Box className='flex h-full flex-col' data-testid='customer-orders.list-page'>
      <Typography variant='h5' className='mb-8 font-normal text-primary'>
        Customer Orders
      </Typography>

      <Box className='flex flex-1 flex-col gap-y-4 overflow-hidden'>
        <Box className='flex items-center gap-x-3'>
          <SearchInput
            placeholder='Search by Customer, SO#, CPO#, Ship From, Ship To, Bill To...'
            className='w-full max-w-[524px]'
            defaultValue={searchParams.query ?? ""}
            onChange={(event) => handleSearchChange(event.target.value)}
            data-testid='customer-orders.list-page.search-field'
          />
          <Button
            appearance='outlined'
            startIcon={<FilterAltOutlined />}
            onClick={() => setIsFilterOpen(true)}
            data-testid='customer-orders.list-page.filter-button'
          >
            Filter
          </Button>
        </Box>

        <CustomerOrderFilterChips searchParams={searchParams} onRemove={handleFilterRemove} />

        <Box className='flex min-h-0 flex-1 overflow-y-auto'>
          <CustomerOrderTable
            data={customerOrders}
            loading={isFetching}
            error={error}
            sorting={sorting}
            expanded={expanded}
            query={searchParams.query}
            statuses={searchParams.statuses}
            dateFrom={searchParams.dateFrom}
            dateTo={searchParams.dateTo}
            dateRangeType={searchParams.dateRangeType}
            containerRef={tableContainerRef}
            onSortingChange={handleSortingChange}
            onExpandedChange={handleExpandedChange}
            onScroll={handleScroll}
            onStartOrder={startOrder}
            canStartOrder={canView}
          />
        </Box>
      </Box>

      <StatusDateRangeFilterDrawer
        open={isFilterOpen}
        searchParams={searchParams}
        onToggle={() => setIsFilterOpen((prev) => !prev)}
        onChange={handleFiltersChange}
        onClear={handleFilterClear}
        statusOptions={CUSTOMER_ORDER_STATUS_OPTIONS}
        dateRangeTypeOptions={CUSTOMER_ORDER_DATE_RANGE_TYPE_OPTIONS}
        testIdPrefix='customer-orders.filter-drawer'
      />
    </Box>
  )
}
